import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useAppSelector } from "@/store";
import { getSocketService } from "@/services/socketService";
import type { SocketService } from "@/services/socketService";
import { notificationService } from "@/services/notificationService";

interface SocketContextType {
  socket: SocketService;
  isConnected: boolean;
  sendMessage: (data: {
    conversationId: string;
    receiverId: string;
    receiverType: "customer" | "agent";
    content: {
      text?: string;
      type: "text" | "image" | "video" | "document" | "file";
      mediaUrl?: string;
      fileName?: string;
      fileSize?: number;
      mimeType?: string;
    };
    replyTo?: string;
  }) => void;
  markAsRead: (messageId: string) => void;
  startTyping: (conversationId: string) => void;
  stopTyping: (conversationId: string) => void;
  joinConversation: (conversationId: string) => void;
  leaveConversation: (conversationId: string) => void;
  updatePresence: (status: "online" | "away" | "busy") => void;
  on: <T = unknown>(event: string, handler: (data: T) => void) => void;
  off: (event: string, handler?: (data: unknown) => void) => void;
}

const SocketContext = createContext<SocketContextType | null>(null);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { token, isAuthenticated, userType } = useAppSelector(
    (state) => state.auth
  );
  const [isConnected, setIsConnected] = useState(false);
  const socketServiceRef = useRef(getSocketService());
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const hasConnectedRef = useRef(false);

  // Connect to socket when authenticated (skip for admin users)
  useEffect(() => {
    // Admin users don't use socket connections
    if (userType === "admin") {
      return;
    }

    if (isAuthenticated && token && !hasConnectedRef.current) {
      // Clear any pending reconnect
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      // Connect to socket
      socketServiceRef.current.connect(token);
      hasConnectedRef.current = true;

      // Listen for connection events
      const handleConnected = () => {
        setIsConnected(true);
      };

      const handleDisconnected = (data: unknown) => {
        const disconnectData = data as { reason: string };
        console.warn("Socket disconnected:", disconnectData.reason);
        setIsConnected(false);

        // Try to reconnect after 3 seconds if not a deliberate disconnect
        if (
          disconnectData.reason !== "io client disconnect" &&
          isAuthenticated &&
          token
        ) {
          reconnectTimeoutRef.current = setTimeout(() => {
            socketServiceRef.current.connect(token);
          }, 3000);
        }
      };

      const handleReconnectFailed = () => {
        console.error("SocketProvider failed to reconnect to socket");
        setIsConnected(false);
      };

      socketServiceRef.current.on("custom:connected", handleConnected);
      socketServiceRef.current.on("custom:disconnected", handleDisconnected);
      socketServiceRef.current.on(
        "custom:reconnect_failed",
        handleReconnectFailed
      );

      return () => {
        socketServiceRef.current.off("custom:connected", handleConnected);
        socketServiceRef.current.off("custom:disconnected", handleDisconnected);
        socketServiceRef.current.off(
          "custom:reconnect_failed",
          handleReconnectFailed
        );
      };
    } else if (!isAuthenticated && hasConnectedRef.current) {
      // Disconnect when logged out
      socketServiceRef.current.disconnect();
      hasConnectedRef.current = false;
      setIsConnected(false);
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [isAuthenticated, token, userType]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (hasConnectedRef.current) {
        socketServiceRef.current.disconnect();
        hasConnectedRef.current = false;
      }
    };
  }, []);

  // Setup notification handler for agents
  useEffect(() => {
    if (!isConnected || userType !== "agent") return;

    // Request notification permission
    notificationService.requestPermission().then((granted) => {
      if (granted) {
        console.log("Notification permission granted");
      } else {
        console.warn("Notification permission denied");
      }
    });

    // Handle incoming messages
    const handleMessageReceived = (data: any) => {
      try {
        const { message } = data;

        if (!message) return;

        // Get sender info
        const senderName = message.senderId?.firstName
          ? `${message.senderId.firstName} ${
              message.senderId.lastName || ""
            }`.trim()
          : "Customer";

        const messageText = message.content?.text || "[Media]";
        const isWhatsApp = message.source === "whatsapp";

        // Trigger notification
        notificationService.notify(
          `${isWhatsApp ? "📱 WhatsApp - " : ""}New message from ${senderName}`,
          {
            body:
              messageText.length > 100
                ? `${messageText.substring(0, 97)}...`
                : messageText,
            playSound: true,
            showNotification: true,
            tag: message.conversationId,
            data: {
              conversationId: message.conversationId,
              messageId: message._id,
            },
          }
        );
      } catch (error) {
        console.error("Error handling message notification:", error);
      }
    };

    // Register listener
    socketServiceRef.current.on("message:received", handleMessageReceived);
    socketServiceRef.current.on("message:new", handleMessageReceived);

    // Cleanup
    return () => {
      socketServiceRef.current.off("message:received", handleMessageReceived);
      socketServiceRef.current.off("message:new", handleMessageReceived);
    };
  }, [isConnected, userType]);

  const sendMessage = useCallback(
    (data: {
      conversationId: string;
      receiverId: string;
      receiverType: "customer" | "agent";
      content: {
        text?: string;
        type: "text" | "image" | "video" | "document" | "file";
        mediaUrl?: string;
        fileName?: string;
        fileSize?: number;
        mimeType?: string;
      };
      replyTo?: string;
    }) => {
      socketServiceRef.current.sendMessage(data);
    },
    []
  );

  const markAsRead = useCallback((messageId: string) => {
    socketServiceRef.current.markMessageAsRead(messageId);
  }, []);

  const startTyping = useCallback((conversationId: string) => {
    socketServiceRef.current.startTyping(conversationId);
  }, []);

  const stopTyping = useCallback((conversationId: string) => {
    socketServiceRef.current.stopTyping(conversationId);
  }, []);

  const joinConversation = useCallback((conversationId: string) => {
    socketServiceRef.current.joinConversation(conversationId);
  }, []);

  const leaveConversation = useCallback((conversationId: string) => {
    socketServiceRef.current.leaveConversation(conversationId);
  }, []);

  const updatePresence = useCallback((status: "online" | "away" | "busy") => {
    socketServiceRef.current.updatePresence(status);
  }, []);

  const on = useCallback(
    <T = unknown,>(event: string, handler: (data: T) => void) => {
      socketServiceRef.current.on(event, handler);
    },
    []
  );

  const off = useCallback(
    (event: string, handler?: (data: unknown) => void) => {
      socketServiceRef.current.off(event, handler);
    },
    []
  );

  const value: SocketContextType = {
    socket: socketServiceRef.current,
    isConnected,
    sendMessage,
    markAsRead,
    startTyping,
    stopTyping,
    joinConversation,
    leaveConversation,
    updatePresence,
    on,
    off,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};

// Custom hook to use socket context
export const useSocketContext = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocketContext must be used within SocketProvider");
  }
  return context;
};
